// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: InjectableCss;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: InjectableCss;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    leftColumnGridItemWithBorder?: GridItemProps;
    fullColumnContainer?: InjectableCss;
    parentContainer?: InjectableCss;
    topWrapper?: InjectableCss;
    middleWrapper?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;            
        `,
    },   
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,           
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<WidgetProps> = props => {
    return (
        <StyledWrapper {...styles.parentContainer}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.rightColumnGridItem} data-test-selector="productDetails_rightColumn">
                    <Hidden {...styles.rightColumnGridItemHidden}>
                        <Zone contentId={props.id} zoneName="Content3" />
                    </Hidden>
                </StyledWrapper>
                <Hidden above="sm">
                    <StyledWrapper {...styles.middleWrapper}>
                        <Zone contentId={props.id} fullHeight={true} zoneName="ProductImagesCarouselMobile" />
                    </StyledWrapper>
                </Hidden>
                <StyledWrapper {...styles.leftColumnGridItemWithBorder} data-test-selector="productDetails_leftColumn">
                    <StyledWrapper {...styles.leftColumnInnerContainer}>
                        <StyledWrapper {...styles.topWrapper}>
                            <Zone contentId={props.id} fullHeight={true} zoneName="Content0" />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.fullColumnContainer}>
                <StyledWrapper {...styles.rightColumnGridItem}>
                    <Zone contentId={props.id} zoneName="Content4" />
                </StyledWrapper>
                <StyledWrapper {...styles.leftColumnGridItem}>
                    <Hidden below="md">
                        <Zone contentId={props.id} zoneName="ProductImagesCarousel" />
                    </Hidden>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
